// The connector policy of a person (« Connecteurs autorisés », SPEC §7), the UI side (pure, no
// DOM): which office connectors the agents may use for the work this person asks for, steers or
// edits, and for his own Claude through a personal MCP key. Mirrors server/connectors/policy.js:
// `{mode: 'all'|'except'|'only', keys: string[]}`, absent = all; an administrator is never
// restricted (PublicUser.connectorPolicy is the effective value). Covered: the detected
// connectors and those of the office's library (« Bibliothèque de connecteurs », `oc-…` / `pc-…`,
// added in the office by address or from the catalogue). Never covered: the Tables du bureau
// (office_tables) and the office's admin connector (Maeck Office). The server enforces it; the UI
// only informs (user form, users table, profile, agent settings) and builds the user form's
// checklist: one row per service, so the Claude and Codex copies of GitHub (claude_ai_GitHub,
// codex-github) are ticked together; a library connector is always a row of its own (two Canva
// accounts are two connectors).
import { t } from '../i18n/fr.js';
import { OFFICE_TABLES_KEY, bareConnectorName, normalizeConnectorName } from './connectoricons.js';

export const POLICY_MODES = Object.freeze(['all', 'except', 'only']);
/** Keys of one policy at most (server/connectors/policy.js MAX_POLICY_KEYS). */
export const MAX_POLICY_KEYS = 50;
const KEY_RE = /^[A-Za-z0-9_-]{1,120}$/;
/**
 * Key of a library connector (server/office/validate.js LIBRARY_KEY_RE): its Claude Code server
 * name, `oc-<neutral id>`, or `pc-<neutral id>` for one added before the library existed.
 */
export const LIBRARY_KEY_RE = /^(?:pc|oc)-[a-z0-9]{6,16}$/;
/** The prefixes reserved to the library: no other connector key uses them. */
const LIBRARY_PREFIX_RE = /^(?:pc|oc)-/;

/** True for the key of a library connector (LIBRARY_KEY_RE). */
export function isLibraryKey(key) {
  return typeof key === 'string' && LIBRARY_KEY_RE.test(key);
}

/** True for a key using a prefix reserved to the library, well-formed or not. */
export function hasLibraryPrefix(key) {
  return typeof key === 'string' && LIBRARY_PREFIX_RE.test(key);
}

/** A fresh « every connector » policy. */
export function allPolicy() {
  return { mode: 'all', keys: [] };
}

/**
 * True for the office's own admin connector, by its key or name: lower-case letters and digits
 * holding both "maeck" and "office" (server/office/validate.js isMaeckOfficeKey).
 * @param {unknown} keyOrName
 */
export function isMaeckOffice(keyOrName) {
  const s = String(keyOrName ?? '').normalize('NFKD').toLowerCase().replace(/[^a-z0-9]/g, '');
  return s.includes('maeck') && s.includes('office');
}

/**
 * True for a connector the policy never covers: the Tables du bureau, the office's admin
 * connector. A library connector (oc-… / pc-…) is covered like any other.
 * @param {unknown} key
 */
export function policyExempt(key) {
  const k = String(key ?? '');
  return k === OFFICE_TABLES_KEY || isMaeckOffice(k);
}

/**
 * A policy in its plain form: unknown or missing → all; 'all' has no key; keys trimmed, valid,
 * deduplicated and sorted, the exempt ones dropped (and a key that only borrows the library's
 * prefix, which the server refuses); 'except' without key → all ('only' without key stays: no
 * office connector at all).
 * @param {unknown} p
 * @returns {{mode: 'all'|'except'|'only', keys: string[]}}
 */
export function normalizePolicy(p) {
  if (!p || typeof p !== 'object' || !POLICY_MODES.includes(p.mode) || p.mode === 'all') return allPolicy();
  const raw = Array.isArray(p.keys) ? p.keys : [];
  const keys = [...new Set(raw.map((k) => (typeof k === 'string' ? k.trim() : '')).filter((k) => KEY_RE.test(k) && !policyExempt(k) && (!hasLibraryPrefix(k) || isLibraryKey(k))))].sort();
  if (p.mode === 'except' && !keys.length) return allPolicy();
  return { mode: p.mode, keys };
}

/** True when the policy restricts something (not « Tous »). */
export function policyRestricts(p) {
  return normalizePolicy(p).mode !== 'all';
}

/** True when two policies say the same (mode and key set). */
export function samePolicy(a, b) {
  const x = normalizePolicy(a);
  const y = normalizePolicy(b);
  return x.mode === y.mode && x.keys.join('\n') === y.keys.join('\n');
}

/**
 * May the agents use the office connector `key` under this policy? Exempt connectors always.
 * @param {unknown} policy
 * @param {unknown} key
 */
export function policyAllows(policy, key) {
  if (policyExempt(key)) return true;
  const p = normalizePolicy(policy);
  if (p.mode === 'except') return !p.keys.includes(String(key));
  if (p.mode === 'only') return p.keys.includes(String(key));
  return true;
}

/**
 * May the agents use `key` for the logged-in person (an administrator always)?
 * @param {{me?: {role?: string, connectorPolicy?: object}|null}} state the store state
 * @param {string} key
 */
export function canUseConnector(state, key) {
  const me = state?.me;
  if (!me || me.role === 'admin') return true;
  return policyAllows(me.connectorPolicy, key);
}

/**
 * The display name of a connector key: its server's name without decoration (« GitHub » for
 * « claude.ai GitHub », « github » for « github (Codex) »; a library connector's label as is),
 * else the key made readable (« connecteur retiré » for a library key no longer listed).
 * @param {string} key
 * @param {Array<{key: string, name?: string, label?: string, library?: boolean}>} [servers]
 */
export function keyLabel(key, servers = []) {
  const k = String(key ?? '');
  const server = (Array.isArray(servers) ? servers : []).find((s) => s && s.key === k);
  if (server?.library) return String(server.label || server.name || '').trim() || k;
  const fromName = server?.name ? bareConnectorName(server.name) : '';
  if (fromName) return fromName;
  if (isLibraryKey(k)) return t('policy.libraryGone');
  return k.replace(/^claude_ai_/i, '').replace(/^codex-/i, '').replace(/_/g, ' ').trim() || k;
}

/**
 * The service a key or server belongs to (« claude.ai GitHub » and « github (Codex) » → github);
 * a library connector is its own (`lib:<key>`), never merged with a detected one of that name.
 */
function serviceOf(server) {
  if (server?.library === true || isLibraryKey(server?.key)) return `lib:${server.key}`;
  return normalizeConnectorName(server?.name || server?.key) || normalizeConnectorName(server?.key) || String(server?.key ?? '');
}

/** The engines of a server of GET /api/connectors ('claude' | 'codex'). */
function enginesOf(server) {
  const list = Array.isArray(server?.engines) ? server.engines.filter((e) => e === 'claude' || e === 'codex') : [];
  if (list.length) return list;
  if (server?.engine === 'codex' || String(server?.key ?? '').startsWith('codex-')) return ['codex'];
  return ['claude'];
}

const STATUS_RANK = { connected: 0, 'needs-auth': 1, failed: 2, error: 2, added: 3, unknown: 3 };

/**
 * The rows of the user form's checklist: one per service among the office connectors — the
 * detected ones (the Claude and Codex copies of a service together) and those of the library
 * (one row each, `library: true`, with who added it) — the built-in Tables du bureau and the
 * admin connector left out, sorted by name; then the keys of `policy` that are no longer listed
 * (`unknown`), so they are never dropped silently.
 * @param {object[]} servers GET /api/connectors servers
 * @param {unknown} [policy]
 * @returns {Array<{id: string, label: string, keys: string[], servers: object[], engines: string[], status: string,
 *   unknown: boolean, library: boolean, addedByName: string}>}
 */
export function policyChoices(servers, policy) {
  const groups = new Map();
  const list = (Array.isArray(servers) ? servers : [])
    .filter((s) => s && typeof s.key === 'string' && s.key && s.builtin !== true && !policyExempt(s.key) && !isMaeckOffice(s.name))
    // The Claude copy first, so it names the row (« GitHub » rather than « github »).
    .sort((a, b) => Number(enginesOf(a)[0] === 'codex') - Number(enginesOf(b)[0] === 'codex'));
  for (const s of list) {
    const id = serviceOf(s);
    let g = groups.get(id);
    if (!g) {
      const library = s.library === true || isLibraryKey(s.key);
      g = {
        id, label: library ? keyLabel(s.key, [{ ...s, library: true }]) : bareConnectorName(s.name || s.key) || s.key,
        keys: [], servers: [], engines: [], status: 'unknown', unknown: false,
        library, addedByName: library && typeof s.addedByName === 'string' ? s.addedByName.trim() : '',
      };
      groups.set(id, g);
    }
    if (g.keys.includes(s.key)) continue;
    g.keys.push(s.key);
    g.servers.push(s);
    for (const e of enginesOf(s)) if (!g.engines.includes(e)) g.engines.push(e);
    const st = typeof s.status === 'string' ? s.status : 'unknown';
    if ((STATUS_RANK[st] ?? 9) < (STATUS_RANK[g.status] ?? 9)) g.status = st;
  }
  const rows = [...groups.values()].sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  const known = new Set(rows.flatMap((r) => r.keys));
  for (const key of normalizePolicy(policy).keys) {
    if (known.has(key)) continue;
    rows.push({
      id: `key:${key}`, label: keyLabel(key), keys: [key], servers: [], engines: [], status: 'missing', unknown: true,
      library: isLibraryKey(key), addedByName: '',
    });
  }
  return rows;
}

/**
 * The names of a policy's connectors, one per service (« GitHub » once for claude_ai_GitHub and
 * codex-github; each library connector by its own name), sorted.
 * @param {unknown} policy
 * @param {object[]} [servers]
 * @returns {string[]}
 */
export function policyNames(policy, servers = []) {
  const seen = new Map();
  for (const key of normalizePolicy(policy).keys) {
    const label = keyLabel(key, servers);
    const id = isLibraryKey(key) ? `lib:${key}` : normalizeConnectorName(label) || label;
    if (!seen.has(id)) seen.set(id, label);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
}

/**
 * A short French summary: « tous », « tous sauf GitHub », « seulement ClickUp, Gmail », « aucun ».
 * @param {unknown} policy
 * @param {object[]} [servers] to name the keys
 */
export function policySummary(policy, servers = []) {
  const p = normalizePolicy(policy);
  if (p.mode === 'all') return t('policy.all');
  const list = policyNames(p, servers).join(', ');
  if (p.mode === 'only' && !list) return t('policy.none');
  return t(p.mode === 'except' ? 'policy.except' : 'policy.only', { list });
}
